/** Step 3 — the statement itself: numbered paragraph blocks (Word-style), block library, versions, AI. */
import { useMemo, useState } from "react";
import { History, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ParagraphCard } from "./ParagraphCard";
import { WitnessAiPanel } from "./WitnessAiPanel";
import { toast } from "sonner";
import { applyWitnessAiRun } from "@/lib/api";
import { errMsg, fmtWhen, newParagraph, placeholdersOf } from "./witnessShared";

function AddMenu({ ctx, ws, onAdd, disabled }) {
  const blocks = useMemo(() => (ctx.blocks || []).filter((b) => b.stages === "*" || b.stages.includes(ws.stage)), [ctx.blocks, ws.stage]);
  const fill = (b) => {
    // a block inserted later is filled from the facts the wizard already holds
    const f = { ...ctx.facts, we: ctx.stages.find((s) => s.key === ws.stage)?.we, they: ctx.stages.find((s) => s.key === ws.stage)?.they, deponent_title: ws.deponent?.title, deponent_name: ws.deponent?.name };
    const text = b.text.replace(/\{([a-z_]+)\}/g, (_, k) => (k === "purpose" ? "" : f[k] || `[${k.replace(/_/g, " ")}]`));
    return `<p>${text.replace(/&/g, "&amp;").replace(/</g, "&lt;")}</p>`;
  };
  return (
    <Popover>
      <PopoverTrigger asChild><Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={disabled} data-testid="ws-add-para"><Plus className="w-3 h-3 mr-1" />Add paragraph</Button></PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-1 max-h-80 overflow-y-auto" data-testid="ws-add-menu">
        <button type="button" className="w-full text-left text-[11px] px-2 py-1 rounded hover:bg-slate-100" onClick={() => onAdd(newParagraph("fact", ""))} data-testid="ws-add-blank">Blank paragraph</button>
        <button type="button" className="w-full text-left text-[11px] px-2 py-1 rounded hover:bg-slate-100 font-semibold" onClick={() => onAdd(newParagraph("heading", ""))} data-testid="ws-add-heading">Sub-heading</button>
        <div className="text-[10px] uppercase tracking-wide text-slate-400 px-2 pt-2 pb-1">From the block library</div>
        {blocks.map((b) => (
          <button key={b.id} type="button" className="w-full text-left text-[11px] px-2 py-1 rounded hover:bg-slate-100" title={b.text} onClick={() => onAdd({ ...newParagraph(b.kind, fill(b)), block_id: b.id, title: b.title })} data-testid={`ws-add-block-${b.id}`}>
            {b.title} <span className="text-slate-400">· {b.group}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function Versions({ ws, onRestore, disabled }) {
  const versions = [...(ws.versions || [])].reverse();
  return (
    <Popover>
      <PopoverTrigger asChild><Button size="sm" variant="ghost" className="h-7 text-[11px]" disabled={disabled || !versions.length} data-testid="ws-versions"><History className="w-3 h-3 mr-1" />Versions ({versions.length})</Button></PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-1" data-testid="ws-versions-menu">
        <div className="text-[10px] text-slate-500 px-2 py-1">Every save keeps the previous text (last 12). Restoring keeps the current one as a version too.</div>
        {versions.map((v, i) => (
          <button key={i} type="button" className="w-full text-left text-[11px] px-2 py-1 rounded hover:bg-slate-100 flex justify-between" onClick={() => onRestore(v)} data-testid={`ws-version-${i}`}>
            <span>{fmtWhen(v.at)} · {v.by}</span><span className="text-slate-400">{(v.paragraphs || []).length} ¶</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function WitnessDraft({ caseRow, ctx, ws, onPatch, onReplaceWs, onFlush, disabled }) {
  const paras = ws.paragraphs || [];
  const [, force] = useState(0);
  const setParas = (next) => onPatch({ paragraphs: next });
  const change = (id, patch) => setParas(paras.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const move = (i, d) => { const n = [...paras]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setParas(n); };
  const hasText = paras.some((p) => (p.html || "").replace(/<[^>]+>/g, "").trim());
  const holes = placeholdersOf(paras);
  // the backend writes the reviewed run onto the draft (paragraphs · exhibits · heading fixes · version snapshot) and hands back the statement
  const apply = async (runId, append) => {
    try { await onFlush?.(); const w = await applyWitnessAiRun(caseRow.case_id, ws.id, runId, { append }); onReplaceWs(w); force((x) => x + 1); toast.success(append ? "Appended below your paragraphs" : "Draft replaced — your text is in Versions"); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  let n = 0;
  return (
    <div className="space-y-2">
      <WitnessAiPanel caseId={caseRow.case_id} ws={ws} hasText={hasText} disabled={disabled} onApply={apply} />
      <div className="flex items-center gap-2 flex-wrap">
        <AddMenu ctx={ctx} ws={ws} disabled={disabled} onAdd={(p) => setParas([...paras, p])} />
        <span className="text-[10px] text-slate-500" data-testid="ws-para-count">{paras.filter((p) => p.kind !== "heading").length} numbered paragraphs · exhibits cited: {[...new Set(paras.flatMap((p) => p.exhibits || []))].join(", ") || "none yet"}</span>
        {holes.length > 0 && <span className="text-[10px] text-amber-900 bg-yellow-100 border border-yellow-300 rounded px-1.5 py-0.5" title={holes.join(", ")} data-testid="ws-placeholders">{holes.length} <span className="bg-yellow-200 px-0.5 rounded">[placeholder{holes.length === 1 ? "" : "s"}]</span> to replace — highlighted yellow</span>}
        <span className="flex-1" />
        <Versions ws={ws} disabled={disabled} onRestore={(v) => onPatch({ paragraphs: v.paragraphs, heading: v.heading || ws.heading, exhibits: v.exhibits || ws.exhibits })} />
      </div>
      {!paras.length && <div className="text-[11px] text-muted-foreground border rounded p-3" data-testid="ws-para-empty">No paragraphs yet — add one, pick blocks from the library, or let the AI draft from the file.</div>}
      <div className="space-y-2" data-testid="ws-paragraphs">
        {paras.map((p, i) => {
          if (p.kind !== "heading") n += 1;
          return (
            <ParagraphCard key={p.id} caseId={caseRow.case_id} wsId={ws.id} para={p} number={n} index={i} count={paras.length} exhibits={ws.exhibits || []} disabled={disabled}
              onChange={(patch) => change(p.id, patch)} onMove={(d) => move(i, d)} onRemove={() => setParas(paras.filter((x) => x.id !== p.id))} />
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground">PD 32 para 18: first person, numbered paragraphs, one point each, chronological; say which facts are from own knowledge and which from the records (and the source). Dates and sums in figures. The heading, "will say as follows", statement of truth and signature are added on the next step.</p>
    </div>
  );
}
